import { toast } from '../components/Toast';
import { useAuthContext } from '../context/AuthContext';
import { getProfile, updateProfile, changePassword } from '../api/profile.api';

export default function Profile() {
    const { customer, updateCustomerData } = useAuthContext();

    // Personal Information
    const [profileData, setProfileData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        dateOfBirth: '',
        gender: '',
    });
    const [editing, setEditing] = useState(false);
    const [profileErrors, setProfileErrors] = useState({});
    const [savingProfile, setSavingProfile] = useState(false);
    const [loadingProfile, setLoadingProfile] = useState(true);

    // Change Password
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
    });
    const [passwordErrors, setPasswordErrors] = useState({});
    const [savingPassword, setSavingPassword] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

    useEffect(() => {
        document.title = 'My Profile | Fone Express';
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        setLoadingProfile(true);
        try {
            const res = await getProfile();
            if (res.data?.isOk && res.data?.data) {
                const p = res.data.data;
                setProfileData({
                    firstName: p.firstName || '',
                    lastName: p.lastName || '',
                    email: p.email || '',
                    phone: p.phone || '',
                    dateOfBirth: p.dateOfBirth ? p.dateOfBirth.split('T')[0] : '',
                    gender: p.gender || '',
                });
            }
        } catch {
            // Fall back to context data
            if (customer) {
                setProfileData({
                    firstName: customer.firstName || '',
                    lastName: customer.lastName || '',
                    email: customer.email || '',
                    phone: customer.phone || '',
                    dateOfBirth: customer.dateOfBirth ? customer.dateOfBirth.split('T')[0] : '',
                    gender: customer.gender || '',
                });
            }
        } finally {
            setLoadingProfile(false);
        }
    };

    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setProfileData((prev) => ({ ...prev, [name]: value }));
        if (profileErrors[name]) {
            setProfileErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const validateProfile = () => {
        const newErrors = {};
        if (!profileData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!profileData.lastName.trim()) newErrors.lastName = 'Last name is required';
        setProfileErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleCancelEdit = () => {
        setEditing(false);
        setProfileErrors({});
        // Reset to the original data from context/API
        fetchProfile();
    };

    const handleSaveProfile = async () => {
        if (!validateProfile()) return;

        setSavingProfile(true);
        try {
            const payload = {
                firstName: profileData.firstName.trim(),
                lastName: profileData.lastName.trim(),
                dateOfBirth: profileData.dateOfBirth || undefined,
                gender: profileData.gender || undefined,
            };
            const res = await updateProfile(payload);
            if (res.data?.isOk) {
                toast.success('Profile updated successfully');
                setEditing(false);
                // Update context with new data
                updateCustomerData(res.data.data || payload);
            } else {
                toast.error(res.data?.message || 'Failed to update profile');
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Something went wrong. Please try again.');
        } finally {
            setSavingProfile(false);
        }
    };

    // Password handlers
    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData((prev) => ({ ...prev, [name]: value }));
        if (passwordErrors[name]) {
            setPasswordErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const validatePassword = () => {
        const newErrors = {};

        if (!passwordData.currentPassword) {
            newErrors.currentPassword = 'Current password is required';
        }
        if (!passwordData.newPassword) {
            newErrors.newPassword = 'New password is required';
        } else if (passwordData.newPassword.length < 8) {
            newErrors.newPassword = 'Password must be at least 8 characters';
        } else if (!/[A-Z]/.test(passwordData.newPassword)) {
            newErrors.newPassword = 'Must contain at least one uppercase letter';
        } else if (!/[a-z]/.test(passwordData.newPassword)) {
            newErrors.newPassword = 'Must contain at least one lowercase letter';
        } else if (!/\d/.test(passwordData.newPassword)) {
            newErrors.newPassword = 'Must contain at least one digit';
        } else if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(passwordData.newPassword)) {
            newErrors.newPassword = 'Must contain at least one special character';
        } else if (passwordData.newPassword === passwordData.currentPassword) {
            newErrors.newPassword = 'New password must be different from current password';
        }

        if (!passwordData.confirmNewPassword) {
            newErrors.confirmNewPassword = 'Please confirm your new password';
        } else if (passwordData.newPassword !== passwordData.confirmNewPassword) {
            newErrors.confirmNewPassword = 'Passwords do not match';
        }

        setPasswordErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (!validatePassword()) return;

        setSavingPassword(true);
        try {
            const res = await changePassword({
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword,
            });
            if (res.data?.isOk) {
                toast.success('Password changed successfully');
                setPasswordData({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
                setShowCurrentPassword(false);
                setShowNewPassword(false);
                setShowConfirmNewPassword(false);
            } else {
                toast.error(res.data?.message || 'Failed to change password');
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Something went wrong. Please try again.');
        } finally {
            setSavingPassword(false);
        }
    };

    if (loadingProfile) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-10 h-10 border-4 border-border border-t-primary rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Section 1: Personal Information */}
            <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
                <div className="p-5 border-b border-border flex items-center justify-between">
                    <h2 className="text-lg font-bold text-foreground">Personal Information</h2>
                    {!editing && (
                        <button
                            onClick={() => setEditing(true)}
                            className="px-4 py-2 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary/5 transition-colors"
                        >
                            <i className="ri-pencil-line mr-1"></i>
                            Edit
                        </button>
                    )}
                </div>

                <div className="p-5">
                    {editing ? (
                        /* Edit Mode */
                        <div className="space-y-5">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1.5">
                                        First Name
                                    </label>
                                    <input
                                        name="firstName"
                                        type="text"
                                        value={profileData.firstName}
                                        onChange={handleProfileChange}
                                        className={`w-full px-4 py-3 border rounded-lg text-sm outline-none transition-colors ${
                                            profileErrors.firstName
                                                ? 'border-red-500 focus:border-red-500'
                                                : 'border-border focus:border-primary'
                                        }`}
                                    />
                                    {profileErrors.firstName && (
                                        <p className="mt-1 text-xs text-red-500">{profileErrors.firstName}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1.5">
                                        Last Name
                                    </label>
                                    <input
                                        name="lastName"
                                        type="text"
                                        value={profileData.lastName}
                                        onChange={handleProfileChange}
                                        className={`w-full px-4 py-3 border rounded-lg text-sm outline-none transition-colors ${
                                            profileErrors.lastName
                                                ? 'border-red-500 focus:border-red-500'
                                                : 'border-border focus:border-primary'
                                        }`}
                                    />
                                    {profileErrors.lastName && (
                                        <p className="mt-1 text-xs text-red-500">{profileErrors.lastName}</p>
                                    )}
                                </div>
                            </div>

                            {/* Read-only email and phone */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1.5">
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        value={profileData.email}
                                        disabled
                                        className="w-full px-4 py-3 border border-border rounded-lg text-sm bg-muted text-muted-foreground cursor-not-allowed"
                                    />
                                    <p className="mt-1 text-xs text-muted-foreground">Contact support to change</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1.5">
                                        Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        value={profileData.phone}
                                        disabled
                                        className="w-full px-4 py-3 border border-border rounded-lg text-sm bg-muted text-muted-foreground cursor-not-allowed"
                                    />
                                    <p className="mt-1 text-xs text-muted-foreground">Contact support to change</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1.5">
                                        Date of Birth
                                    </label>
                                    <input
                                        name="dateOfBirth"
                                        type="date"
                                        value={profileData.dateOfBirth}
                                        onChange={handleProfileChange}
                                        className="w-full px-4 py-3 border border-border rounded-lg text-sm outline-none focus:border-primary transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1.5">
                                        Gender
                                    </label>
                                    <select
                                        name="gender"
                                        value={profileData.gender}
                                        onChange={handleProfileChange}
                                        className="w-full px-4 py-3 border border-border rounded-lg text-sm outline-none focus:border-primary transition-colors cursor-pointer"
                                    >
                                        <option value="">Select gender</option>
                                        <option value="male">Male</option>
                                        <option value="female">Female</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-3 pt-2">
                                <button
                                    onClick={handleSaveProfile}
                                    disabled={savingProfile}
                                    className="px-6 py-2.5 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {savingProfile && (
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    )}
                                    {savingProfile ? 'Saving...' : 'Save Changes'}
                                </button>
                                <button
                                    onClick={handleCancelEdit}
                                    disabled={savingProfile}
                                    className="px-6 py-2.5 border border-border text-muted-foreground rounded-lg text-sm font-medium hover:bg-muted transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* Display Mode */
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div>
                                <p className="text-xs text-muted-foreground mb-1">Full Name</p>
                                <p className="text-sm font-medium text-foreground">
                                    {profileData.firstName} {profileData.lastName}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground mb-1">Email Address</p>
                                <p className="text-sm font-medium text-foreground">
                                    {profileData.email || '-'}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground mb-1">Phone Number</p>
                                <p className="text-sm font-medium text-foreground">
                                    {profileData.phone || '-'}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground mb-1">Date of Birth</p>
                                <p className="text-sm font-medium text-foreground">
                                    {profileData.dateOfBirth
                                        ? new Date(profileData.dateOfBirth).toLocaleDateString('en-GB', {
                                              day: 'numeric',
                                              month: 'long',
                                              year: 'numeric',
                                          })
                                        : '-'}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground mb-1">Gender</p>
                                <p className="text-sm font-medium text-foreground capitalize">
                                    {profileData.gender || '-'}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Section 2: Change Password */}
            <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
                <div className="p-5 border-b border-border">
                    <h2 className="text-lg font-bold text-foreground">Change Password</h2>
                </div>

                <div className="p-5">
                    <form onSubmit={handleChangePassword} className="space-y-5 max-w-md" autoComplete="off">
                        {/* Stop browsers from filling a saved login password into Current Password */}
                        <input
                            type="text"
                            name="username"
                            autoComplete="username"
                            tabIndex={-1}
                            aria-hidden="true"
                            className="absolute -left-[9999px] h-0 w-0 opacity-0"
                        />
                        <input
                            type="password"
                            name="fake-password"
                            autoComplete="new-password"
                            tabIndex={-1}
                            aria-hidden="true"
                            className="absolute -left-[9999px] h-0 w-0 opacity-0"
                        />
                        {/* Current Password */}
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1.5">
                                Current Password
                            </label>
                            <div className="relative">
                                <input
                                    name="currentPassword"
                                    type={showCurrentPassword ? 'text' : 'password'}
                                    autoComplete="off"
                                    data-lpignore="true"
                                    data-1p-ignore="true"
                                    readOnly
                                    onFocus={(e) => e.target.removeAttribute('readonly')}
                                    value={passwordData.currentPassword}
                                    onChange={handlePasswordChange}
                                    placeholder="Enter current password"
                                    className={`w-full px-4 py-3 pr-12 border rounded-lg text-sm outline-none transition-colors ${
                                        passwordErrors.currentPassword
                                            ? 'border-red-500 focus:border-red-500'
                                            : 'border-border focus:border-primary'
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                    tabIndex={-1}
                                >
                                    <i className={showCurrentPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                                </button>
                            </div>
                            {passwordErrors.currentPassword && (
                                <p className="mt-1 text-xs text-red-500">{passwordErrors.currentPassword}</p>
                            )}
                        </div>

                        {/* New Password */}
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1.5">
                                New Password
                            </label>
                            <div className="relative">
                                <input
                                    name="newPassword"
                                    type={showNewPassword ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    value={passwordData.newPassword}
                                    onChange={handlePasswordChange}
                                    placeholder="Min 8 characters"
                                    className={`w-full px-4 py-3 pr-12 border rounded-lg text-sm outline-none transition-colors ${
                                        passwordErrors.newPassword
                                            ? 'border-red-500 focus:border-red-500'
                                            : 'border-border focus:border-primary'
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                    tabIndex={-1}
                                >
                                    <i className={showNewPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                                </button>
                            </div>
                            {passwordErrors.newPassword ? (
                                <p className="mt-1 text-xs text-red-500">{passwordErrors.newPassword}</p>
                            ) : (
                                <p className="mt-1 text-xs text-muted-foreground">
                                    Must include uppercase, lowercase, digit, and special character
                                </p>
                            )}
                        </div>

                        {/* Confirm New Password */}
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1.5">
                                Confirm New Password
                            </label>
                            <div className="relative">
                                <input
                                    name="confirmNewPassword"
                                    type={showConfirmNewPassword ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    value={passwordData.confirmNewPassword}
                                    onChange={handlePasswordChange}
                                    placeholder="Re-enter new password"
                                    className={`w-full px-4 py-3 pr-12 border rounded-lg text-sm outline-none transition-colors ${
                                        passwordErrors.confirmNewPassword
                                            ? 'border-red-500 focus:border-red-500'
                                            : 'border-border focus:border-primary'
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                    tabIndex={-1}
                                >
                                    <i className={showConfirmNewPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                                </button>
                            </div>
                            {passwordErrors.confirmNewPassword && (
                                <p className="mt-1 text-xs text-red-500">{passwordErrors.confirmNewPassword}</p>
                            )}
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={savingPassword}
                            className="px-6 py-2.5 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {savingPassword && (
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            )}
                            {savingPassword ? 'Changing...' : 'Change Password'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
